import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import ficha from "../img/ficha.png";
import { plantelDeMatricula } from "../utilities/plantel";

// --- 3. MODULARIZACIÓN DE ESTILOS (DRY) ---
const absoluteText = { position: "absolute", left: "5.75cm", fontSize: "10px" };
const convenioBase = { position: "absolute", left: "1.5cm", fontSize: "8px" };
const flexRow = { display: "flex", flexDirection: "row", justifyContent: "space-between" };

const styles = StyleSheet.create({
  page: { margin: "0", padding: "0" },
  back: { zIndex: "-1", position: "absolute", width: "100%", height: "100%" },
  name: { ...absoluteText, top: "5cm" },
  campus: { ...absoluteText, top: "6.7cm" },
  code: { ...absoluteText, top: "6.7cm", left: "13.3cm" },
  convenioBBVA: { ...convenioBase, top: "11.2cm" },
  convenioBanorte: { ...convenioBase, top: "12.4cm" },
  section: {
    position: "absolute",
    top: "12cm",
    left: "5.75cm",
    fontSize: "8px",
    width: "14.5cm",
  },
  section2: {
    ...flexRow,
    marginBottom: "15px",
  },
  section2Titles: {
    ...flexRow,
    marginBottom: "10px",
    color: "#bbbdbf",
    textTransform: "uppercase",
  },
  concepto: { width: "4.5cm", marginLeft: "10px" },
  referenciasContenedor: { width: "7cm", display: "flex", flexDirection: "column" },
  bancoTitulo: { fontFamily: "Helvetica-Bold", fontSize: "7px", color: "#555", marginBottom: "2px" },
  referenciaNumero: { fontSize: "8px", marginBottom: "8px" },
  vigencia: { fontSize: "7px", color: "#8A4B10", marginTop: "-6px", marginBottom: "8px" },
  precio: { width: "1.5cm", textAlign: "right" },
});

// Catálogo de planteles
const listaPlanteles = {
  "01": "TESISTÁN", "02": "LA DURAZNERA (TLAQUEPAQUE)", "03": "TEPATITLÁN",
  "04": "COCULA", "05": "EL SALTO", "06": "TOTATICHE", "07": "PUERTO VALLARTA",
  "08": "IXTLAHUACÁN DEL RÍO", "09": "VALLE DE JUÁREZ", "10": "ENCARNACIÓN DE DÍAZ",
  "11": "ATOTONILCO EL ALTO", "12": "EL GRULLO", "13": "CIHUATLÁN",
  "14": "ZAPOTILTIC", "15": "GUADALAJARA", "16": "TLAJOMULCO DE ZUÑIGA",
  "17": "EL ARENAL", "18": "SANTA ANITA", "19": "NEXTIPAC", "20": "TECALITLÁN",
  "21": "TLAJOMULCO II", "22": "SAN IGNACIO CERRO GORDO", "23": "TLAJOMULCO SANTA FE",
  "24": "PUERTO VALLARTA - IXTAPA", "25": "ZAPOPAN - SANTA MARGARITA", "26": "TONALÁ - EL PANORÁMICO",
  "27": "IXTLAHUACÁN DE LOS MEMBRILLOS", "28": "LAGOS DE MORENO", "29": "SAN JUAN DE LOS LAGOS",
  "30": "SAN PEDRO TLAQUEPAQUE", "31": "TALA",
};

// --- 4. SIMPLIFICACIÓN GRAMATICAL ---
const formatearListaGramatical = (lista) => {
  if (lista.length === 0) return "";
  if (lista.length === 1) return lista[0];
  return `${lista.slice(0, -1).join(", ")} y ${lista[lista.length - 1]}`;
};

const agruparParaPDF = (tabla) => {
  const grupos = {};
  tabla.forEach((item) => {
    const refBanorte = item.referencias?.Banorte || "S/R";
    const refBBVA = item.referencias?.BBVA || "S/R";
    const folioTPV = item.folioTPV || null;

    if (!grupos[refBanorte]) {
      grupos[refBanorte] = { conceptosRaw: [], total: 0, refBanorte, refBBVA, folioTPV };
    }
    grupos[refBanorte].conceptosRaw.push(item.concepto);
    grupos[refBanorte].total += Number(item.precio);
  });

  return Object.values(grupos).map((grupo) => {
    // Comparacion sin distinguir mayusculas ni singular/plural
    const esDiploma = (c) => String(c).toLowerCase().includes("diploma");

    const diplomasCount = grupo.conceptosRaw.filter(esDiploma).length;
    const conceptosList = grupo.conceptosRaw.filter((c) => !esDiploma(c));

    if (diplomasCount === 1) conceptosList.push("Diploma de competencias");
    if (diplomasCount > 1) conceptosList.push(`Diplomas de competencias (${diplomasCount})`);

    return {
      concepto: formatearListaGramatical(conceptosList),
      refBanorte: grupo.refBanorte,
      refBBVA: grupo.refBBVA,
      folioTPV: grupo.folioTPV,
      precio: grupo.total,
    };
  });
};

// YA NO RECIBE "folioRegistro" COMO VARIABLE GENERAL
// plantelOverride/nombrePlantelOverride: para matriculas ficticias (ej. ex-alumnos)
// donde el plantel no se puede derivar de la matricula, sino que ya se conoce
// de antemano (la caja/sesion del cajero). Si no se pasan, el comportamiento
// para alumnos reales no cambia.
const Pdf = ({ name, code, table, fechaVencimiento, plantelOverride, nombrePlantelOverride }) => {
  const plantel = plantelOverride || plantelDeMatricula(code);
  const nombrePlantel = nombrePlantelOverride || listaPlanteles[plantel] || "";
  const tablaAgrupada = agruparParaPDF(table);

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <Image src={ficha} style={styles.back} />
        <Text style={styles.name}>{name}</Text>
        <Text style={styles.campus}>
          {plantel} {nombrePlantel}
        </Text>
        <Text style={styles.code}>{code}</Text>
        <Text style={styles.convenioBBVA}>CIE: 1806998</Text>
        <Text style={styles.convenioBanorte}>Servicio 5446</Text>
        <View style={styles.section}>
          <View style={styles.section2Titles}>
            <Text style={styles.concepto}>Concepto</Text>
            <Text style={{ width: "7cm" }}>Referencias</Text>
            <Text style={styles.precio}>Monto</Text>
          </View>
          {tablaAgrupada.map((grupo, index) => (
            <View style={styles.section2} key={index}>
              <Text style={styles.concepto}>{grupo.concepto}</Text>
              
              <View style={styles.referenciasContenedor}>
                <Text style={styles.bancoTitulo}>BANORTE:</Text>
                <Text style={styles.referenciaNumero}>{grupo.refBanorte}</Text>
                {fechaVencimiento ? (
                  <Text style={styles.vigencia}>Vigente hasta el {fechaVencimiento}</Text>
                ) : null}
                <Text style={styles.bancoTitulo}>BBVA:</Text>
                <Text style={styles.referenciaNumero}>{grupo.refBBVA}</Text>
                
                {/* --- AHORA IMPRIME EL FOLIO INDIVIDUAL DE CADA GRUPO --- */}
                {grupo.folioTPV ? (
                  <>
                    <Text style={styles.bancoTitulo}>TPV (Plantel):</Text>
                    <Text style={styles.referenciaNumero}>{grupo.folioTPV}</Text>
                  </>
                ) : null}
              </View>
              
              <Text style={styles.precio}>
                ${Number(grupo.precio).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Text>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default Pdf;